import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Download, Eye, MousePointerClick, Target } from "lucide-react";
import { toast } from "sonner";

import AdminHeader from "@/components/admin/AdminHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { getAdCustomerDetail } from "@/api/ads";

function metricNumber(value, suffix = "") {
  return `${new Intl.NumberFormat("sv-SE").format(Number(value || 0))}${suffix}`;
}

function downloadCsv(filename, rows) {
  const csv = rows
    .map((row) => row.map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`).join(";"))
    .join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function StatCard({ icon: Icon, title, value, hint }) {
  return (
    <Card className="border-border/80 shadow-sm">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm text-muted-foreground">{title}</div>
            <div className="mt-2 text-3xl font-semibold text-foreground">{value}</div>
            {hint ? <div className="mt-1 text-xs text-muted-foreground">{hint}</div> : null}
          </div>
          <div className="rounded-full border border-border/80 bg-muted/40 p-2.5 text-muted-foreground">
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdCustomerDetailPage() {
  const { customerName = "" } = useParams();
  const decodedCustomerName = decodeURIComponent(customerName);
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [dateFrom, setDateFrom] = useState(searchParams.get("date_from") || "");
  const [dateTo, setDateTo] = useState(searchParams.get("date_to") || "");

  useEffect(() => {
    let ignore = false;
    async function load() {
      try {
        setLoading(true);
        const data = await getAdCustomerDetail(decodedCustomerName, {
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        });
        if (!ignore) setDetail(data);
      } catch (err) {
        if (!ignore) {
          toast.error("Kunde inte hämta kundrapporten");
          setDetail(null);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    const nextParams = {};
    if (dateFrom) nextParams.date_from = dateFrom;
    if (dateTo) nextParams.date_to = dateTo;
    setSearchParams(nextParams, { replace: true });
    return () => {
      ignore = true;
    };
  }, [decodedCustomerName, dateFrom, dateTo, setSearchParams]);

  const csvRows = useMemo(() => {
    const ads = detail?.ads || [];
    return [
      ["Kund", "Annons", "Ytor", "Målgrupp", "Status", "Moderering", "Visningar", "Klick", "CTR"],
      ...ads.map((ad) => [
        detail?.customer_name || "",
        ad.title,
        Array.isArray(ad.placements) && ad.placements.length ? ad.placements.join(", ") : ad.placement,
        ad.target_mode === "all" ? "Nationell" : (ad.club_slugs || []).join(", "),
        ad.status,
        ad.moderation_status,
        ad.impressions,
        ad.clicks,
        `${ad.ctr}%`,
      ]),
    ];
  }, [detail]);

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader />
      <main className="container mx-auto px-4 py-6 space-y-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div className="space-y-1">
            <Button asChild variant="ghost" className="px-0">
              <Link to="/admin/ads" className="inline-flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Tillbaka till annonser
              </Link>
            </Button>
            <h1 className="text-2xl font-semibold text-foreground">{decodedCustomerName}</h1>
            <p className="text-sm text-muted-foreground">Detaljrapport för kundens annonser.</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            <Button asChild variant="outline">
              <Link to={`/admin/ads/new?customer=${encodeURIComponent(decodedCustomerName)}`}>
                Ny annons för kund
              </Link>
            </Button>
            <Button variant="outline" onClick={() => downloadCsv(`annonser-${decodedCustomerName}.csv`, csvRows)}>
              <Download className="mr-2 h-4 w-4" />
              Exportera CSV
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="grid gap-4 md:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => <Skeleton key={index} className="h-32 w-full" />)}
          </div>
        ) : (
          <>
            <div className="grid gap-4 md:grid-cols-3">
              <StatCard icon={Eye} title="Visningar" value={metricNumber(detail?.total_impressions)} />
              <StatCard icon={MousePointerClick} title="Klick" value={metricNumber(detail?.total_clicks)} />
              <StatCard icon={Target} title="CTR" value={metricNumber(detail?.ctr, "%")} hint={`${metricNumber(detail?.total_ads)} annonser`} />
            </div>

            <Card className="min-w-0 border-border/80 shadow-sm">
              <CardHeader>
                <CardTitle>Annonslista</CardTitle>
                <CardDescription>Visar kundens annonser inom vald period.</CardDescription>
              </CardHeader>
              <CardContent className="min-w-0">
                {!detail?.ads?.length ? (
                  <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                    Ingen annonsdata för vald period.
                  </div>
                ) : (
                  <div className="w-full overflow-x-auto">
                  <Table className="min-w-[820px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Annons</TableHead>
                        <TableHead>Ytor</TableHead>
                        <TableHead>Målgrupp</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Visningar</TableHead>
                        <TableHead>Klick</TableHead>
                        <TableHead>CTR</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detail.ads.map((ad) => (
                        <TableRow key={ad.id}>
                          <TableCell className="font-medium">{ad.title}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {Array.isArray(ad.placements) && ad.placements.length ? ad.placements.join(", ") : ad.placement}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {ad.target_mode === "all" ? "Nationell" : (ad.club_slugs || []).join(", ")}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-2">
                              <Badge variant="outline">{ad.status}</Badge>
                              <Badge variant="outline">{ad.moderation_status}</Badge>
                            </div>
                          </TableCell>
                          <TableCell>{metricNumber(ad.impressions)}</TableCell>
                          <TableCell>{metricNumber(ad.clicks)}</TableCell>
                          <TableCell>{metricNumber(ad.ctr, "%")}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
